import React from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import SmartScrollRow from "./SmartScrollRow";
import { isCategoryVisible } from "../../utils/categoryVisibility";
import "./MobileCategoryPills.scss";

// Recursive DFS over the nested category tree (same shape NAVIGATOR uses).
function findInTree(nodes, slug) {
  if (!nodes || !slug) return null;
  for (const cat of nodes) {
    if (cat.slug === slug) return cat;
    const found = findInTree(cat.children, slug);
    if (found) return found;
  }
  return null;
}

const PILL_WIDTHS = [72, 96, 84, 108, 76, 92];

/** Placeholder mobile subcategory pills while products load. */
export const MobileCategoryPillsSkeleton = () => (
  <div className="mobile-category-pills-slot" aria-hidden="true">
    <div className="mobile-category-pills__track">
      {PILL_WIDTHS.map((width, index) => (
        <span
          key={index}
          className="skeleton mobile-category-pills-skeleton__pill"
          style={{ width: `${width}px` }}
        />
      ))}
    </div>
  </div>
);

const MobileCategoryPills = ({
  selectedSlug,
  onSelect,
  setPendingScrollSlug,
}) => {
  const categories = useSelector((state) => state.category.categories);
  const itemsShow = useSelector((state) => state.displayProduct.itemsShow);
  const includeInactive = useSelector(
    (state) => state.category.includeInactive,
  );
  const role = useSelector((state) => state.user.role);
  const { id } = useParams();

  if (!id) return null;

  const resolve = (slug) => {
    if (!slug) return null;
    return (
      findInTree(categories, slug) ||
      (itemsShow?.slug === slug ? itemsShow : null)
    );
  };

  // Reflect the pill the user tapped or scroll-spy selection; fall back to the page category.
  const current = resolve(selectedSlug) || resolve(id);
  if (!current) return null;

  // Show this category's children if it has any; otherwise its siblings (neighbours).
  const hasChildren = current.children?.some((cat) => !cat.isFamily);
  const list = hasChildren
    ? current.children
    : resolve(current.parentSlug)?.children || [];

  const pills = list.filter((cat) =>
    isCategoryVisible(cat, role, includeInactive),
  );
  if (pills.length === 0) return null;

  return (
    <div className="mobile-category-pills-slot">
      <SmartScrollRow
        rtl
        shrinkToContent
        trackClassName="mobile-category-pills__track"
      >
        {pills.map((cat) => (
          <button
            key={cat.slug}
            type="button"
            className={`mobile-category-pill${
              cat.slug === selectedSlug ? " mobile-category-pill--active" : ""
            }`}
            onClick={() => {
              onSelect(cat.slug);
              setPendingScrollSlug(cat.slug);
            }}
          >
            {cat.name}
          </button>
        ))}
      </SmartScrollRow>
    </div>
  );
};

export default MobileCategoryPills;
